import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Set Content-Type to application/json for non-FormData requests
    // Let browser set the boundary for multipart/form-data automatically
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip retry for refresh-token endpoint to avoid infinite loops
    const isAuthEndpoint = originalRequest.url?.includes('/auth/refresh-token');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await api.post('/auth/refresh-token');
        const { accessToken } = response.data;
        localStorage.setItem('accessToken', accessToken);

        onRefreshed(accessToken);
        isRefreshing = false;

        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');

        // Only redirect to login if we're not already on the login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

const authAPI = {
  login: (payload) => api.post('/auth/login', payload),  // payload can contain email or phone with authMethod
  verifyOTP: (payload) => api.post('/auth/verify-otp', payload),  // payload contains email/phone, otp, and authMethod
  verify: () => api.get('/auth/verify'),
  resendOTP: (payload) => api.post('/auth/resend-otp', payload),  // payload contains email/phone and authMethod
  logout: () => api.post('/auth/logout'),
  refreshToken: () => api.post('/auth/refresh-token'),
};

const companyAPI = {
  // Company intake - expects FormData
  completeIntake: (formData) => api.post('/company/intake', formData),
  getCompanyInfo: () => api.get('/company/info'),

  getDashboard: () => api.get('/company/dashboard'),
  getProfile: () => api.get('/company/profile'),
  updateProfile: (data) => api.put('/company/profile', data),

  // Company details
  saveCompanyDetails: (data) => api.post('/company/details', data),
  updateCompanyDetails: (formData) => api.put('/company/details', formData),
  getCompanyDetails: () => api.get('/company/details'),

  // Networking passes (simplified - no pass codes)
  saveNetworkingPasses: (data) => api.post('/company/networking-passes', data),
  updateNetworkingPasses: (data) => api.put('/company/networking-passes', data),
  getNetworkingPasses: () => api.get('/company/networking-passes'),
  claimNetworkingPass: (passType, attendeeData) => api.post('/company/networking-passes/claim', { passType, ...attendeeData }),

  // Presentation
  uploadPresentation: (formData) => api.post('/company/presentation', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updatePresentation: (formData) => api.put('/company/presentation', formData),
  getPresentation: () => api.get('/company/presentation'),

  // Delete test data (for internal testing only)
  deleteTestData: () => api.delete('/company/delete-test-data'),

  // Demo Guidelines
  getGuidelinesStatus: () => api.get('/company/guidelines/status'),
  markGuidelinesAsRead: (status = true) => api.put('/company/guidelines/mark-read', { status }),

  // Pitch Deck Feedback
  getPitchDeckFeedback: () => api.get('/company/pitch-deck-feedback'),
  submitPitchDeckFeedback: (formData, config) => api.post('/company/pitch-deck-feedback', formData, config),
  updatePitchDeckFeedback: (formData, config) => api.put('/company/pitch-deck-feedback', formData, config),

  // Brand Promotion
  getBrandPromotion: () => api.get('/company/brand-promotion'),
  saveBrandPromotion: (data) => api.post('/company/brand-promotion', data),
  updateBrandPromotion: (data) => api.put('/company/brand-promotion', data),

  // Technical Checklist
  getTechnicalChecklist: () => api.get('/company/technical-checklist'),
  updateTechnicalChecklistItem: (itemId, checked) =>
    api.put('/company/technical-checklist/item', { itemId, checked }),
  updateAllTechnicalChecklistItems: (completed) =>
    api.put('/company/technical-checklist/all', { completed }),

  // Promotional Flyer
  getPromotionalFlyerStatus: () => api.get('/company/promotional-flyer'),
  updatePromotionalFlyer: (data) => api.put('/company/promotional-flyer', data),

  // Resource Links
  getResourceLinks: () => api.get('/company/resource-links'),

  // Announcements
  getAnnouncements: () => api.get('/company/announcements')
};

const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getDashboardStats: () => api.get('/admin/dashboard'), // Alias for compatibility
  getUsers: (params) => api.get('/admin/users', { params }),
  getCompanies: () => api.get('/admin/companies'),
  getWhitelist: () => api.get('/admin/whitelist'),
  addToWhitelist: (data) => api.post('/admin/whitelist', data),
  removeFromWhitelist: (email) => api.delete(`/admin/whitelist/${email}`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),

  // Company management
  updateCompany: (id, data) => api.put(`/admin/companies/${id}`, data),
  deleteCompany: (id) => api.delete(`/admin/companies/${id}`),
  toggleNetworkPassesLock: (id) => api.put(`/admin/companies/${id}/toggle-passes-lock`),
  addUserToCompany: (data) => api.post('/admin/companies/add-user', data),
  getCompanyUsers: (companyId) => api.get(`/admin/companies/${companyId}/users`),
  removeUserFromCompany: (companyId, userId) => api.delete(`/admin/companies/${companyId}/users/${userId}`),
  updateUserCompanyRole: (companyId, userId, data) => api.put(`/admin/companies/${companyId}/users/${userId}/role`, data),

  // Company Progress Tracking
  getCompaniesWithProgress: () => api.get('/admin/companies-progress'),
  getCompanyTaskDetails: (companyId) => api.get(`/admin/company/${companyId}/tasks`),
  updateTaskStatus: (taskId, status, notes) => api.put(`/admin/task/${taskId}/status`, { status, notes }),

  // Company Remarks
  getCompanyRemark: (companyId) => api.get(`/admin/companies/${companyId}/remark`),
  updateCompanyRemark: (companyId, data) => api.put(`/admin/companies/${companyId}/remark`, data),

  // Analytics
  getAnalytics: () => api.get('/admin/analytics'),
  getStats: () => api.get('/admin/stats'),

  // Communications
  sendAnnouncement: (data) => api.post('/admin/announcements', data),
  getAnnouncements: () => api.get('/admin/announcements'),

  // Submissions
  getSubmissions: () => api.get('/admin/submissions'),
};

export { authAPI, companyAPI, adminAPI };
export default api;